import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import KurulumRehberi from '../components/KurulumRehberi';
import { config } from '../config/app';
import { useT } from '../i18n';
import { fullAddress, suffixVisible } from '../mail/address';
import { client } from '../api/client';
import { MailError } from '../api/types';
import { useAccounts } from '../auth/store';
import { mailboxPath } from '../mail/urls';
import Brand from '../components/Brand';
import LanguageToggle from '../components/LanguageToggle';

/*
    Sign-in.

    The address and password go to our own backend, which tries them against the mail server and,
    if they are accepted, sets a session cookie. The password is cleared from component state as
    soon as it has been used and is never written anywhere on this side; script cannot read the
    cookie either.

    There is no second factor here because there cannot be: their Dovecot offers PLAIN and LOGIN
    and nothing else. A code box would be offering something the server would ignore.
*/

export default function Login() {
    const { t } = useT();
    const navigate = useNavigate();
    const accounts = useAccounts((s) => s.accounts);
    const adopt = useAccounts((s) => s.adopt);
    const ended = useAccounts((s) => s.ended);
    const brand = config();

    /*
        Here because the session ended, not because somebody signed out: say so, and fill in the
        address so only the password is left to type. Only when no mailbox is open — with one
        still open this form is "add a mailbox", and the toast on the mail screen said it.
    */
    const endedHere = !accounts.length ? ended[0] : undefined;
    const [address, setAddress] = useState(() => {
        if (!endedHere) return '';
        const own = brand.mailDomain ? `@${brand.mailDomain}`.toLowerCase() : '';
        return own && endedHere.toLowerCase().endsWith(own) ? endedHere.slice(0, -own.length) : endedHere;
    });
    const [addressFocused, setAddressFocused] = useState(false);
    // What the deployment will add, and whether it still applies to what has been typed.
    const suffix = suffixVisible(address, brand.mailDomain);
    const [password, setPassword] = useState('');
    // Off by default: the person who forgets to sign out of a shared computer must not leave their
    // mailbox open for a week because of a box they never looked at.
    const [remember, setRemember] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const addressRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        (endedHere ? passwordRef : addressRef).current?.focus();
        // Once, on arrival: moving the cursor later would pull it out of whatever is being typed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (busy) return;
        setError(null);

        if (!fullAddress(address, brand.mailDomain) || !password) {
            setError(t('login.needBoth'));
            return;
        }

        setBusy(true);
        try {
            const who = await client.signIn(fullAddress(address, brand.mailDomain), password, remember);
            // Out of state the moment it has been used. Nothing keeps it after this point.
            setPassword('');
            // Field by field rather than spread, deliberately — but that means a new capability
            // has to be added HERE as well as in the store, and forgetting is silent because the
            // fields are optional. The symptom would be a tab that is missing until a reload.
            const account = adopt(who.address, {
                canPreviewDocuments: who.canPreviewDocuments,
                hasDAV: who.hasDAV,
                hasForwarding: who.hasForwarding,
                hasAdmin: who.hasAdmin,
            });
            navigate(mailboxPath(account.id), { replace: true });
        } catch (err) {
            // The backend answers identically for a wrong password and a mailbox that does not
            // exist, so this cannot say which it was. Saying would turn the form into a way of
            // finding out which addresses are real.
            if (err instanceof MailError && err.status === 429) setError(t('login.tooMany'));
            else if (err instanceof MailError && err.status === 401) setError(t('login.rejected'));
            else setError(t('login.failed'));
        } finally {
            setBusy(false);
        }
    };

    const field: React.CSSProperties = {
        width: '100%',
        height: 38,
        padding: '0 11px',
        background: 'var(--surface2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-input)',
        outline: 'none',
        fontSize: 'var(--fs-md)',
    };

    const label: React.CSSProperties = {
        display: 'block',
        marginBottom: 5,
        fontSize: 'var(--fs-sm)',
        color: 'var(--text-dim)',
    };

    return (
        <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24, background: 'var(--bg)' }}>
            <div style={{ width: 'min(420px, 100%)', display: 'grid', gap: 12 }}>
                <div
                    style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-card)',
                        boxShadow: 'var(--card-shadow)',
                        padding: 22,
                        display: 'grid',
                        gap: 15,
                    }}
                >
                    {/* The mark leads. At 30 it was an afterthought beside the name; the brand's
                        own symbol is drawn to carry a screen, so it is given room to. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)' }}>
                        <Brand size={44} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, letterSpacing: '-0.3px' }}>{brand.productName}</div>
                            {/* Mono kicker değil düz metin: giriş ekranı ürünün ilk cümlesi ve
                                `// giriş yap` bir terminal istemi gibi okunuyordu. */}
                            <div style={{ marginTop: 3, fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>
                                {accounts.length ? t('login.addMailbox') : t('login.signInTo')}
                            </div>
                        </div>
                        <LanguageToggle />
                    </div>

                    {/*
                        Whose address this is.

                        A branded address used to replace the product's name with the company's, so
                        the screen named one thing and left three unexplained. The company is named
                        here instead, as what it is — the operator this deployment belongs to —
                        beside the product rather than in place of it.
                    */}
                    {brand.operatorName || brand.operatorLogo ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--sp-5)',
                                paddingTop: 'var(--sp-4)',
                                borderTop: '1px solid var(--border)',
                            }}
                        >
                            <Brand size={20} of="operator" />
                            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>
                                {brand.operatorName ? t('brand.operatorFor', { name: brand.operatorName }) : null}
                            </span>
                        </div>
                    ) : null}

                    {accounts.length ? (
                        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', lineHeight: 1.55, color: 'var(--text-dim)' }}>
                            {t('login.intro')}
                        </p>
                    ) : null}

                    {endedHere ? (
                        <div
                            role="status"
                            data-testid="session-ended"
                            style={{
                                padding: '9px 11px',
                                borderRadius: 'var(--r-btn)',
                                background: 'var(--amber-bg)',
                                border: '1px solid var(--amber)',
                                color: 'var(--text)',
                                fontSize: 'var(--fs-sm)',
                            }}
                        >
                            {t('login.sessionEnded')}
                        </div>
                    ) : null}

                    <form onSubmit={submit} style={{ display: 'grid', gap: 11 }}>
                        <div>
                            <label htmlFor="mail-address" style={label}>{t('login.address')}</label>
                            {/*
                                The field is a row, not a box with something painted on it.

                                The suffix sits beside the input as its own element, so nothing has
                                to be measured and the text can never run underneath it. The border
                                belongs to the row; the input inside is bare.
                            */}
                            <div
                                style={{
                                    ...field,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0,
                                    outline: addressFocused ? '2px solid var(--accent)' : 'none',
                                    outlineOffset: 2,
                                }}
                            >
                                <input
                                    id="mail-address"
                                    ref={addressRef}
                                    /*
                                        Not type="email" when a domain is declared.

                                        The browser's own check insists on an @, and on these
                                        deployments the field is meant to hold a name without one —
                                        so typing "info" produced a warning about an address that
                                        was going to be perfectly valid a moment later. inputMode
                                        keeps the @ key on a phone keyboard, which is the part of
                                        type="email" that was actually worth having.

                                        Where no domain is declared the field really does want a
                                        whole address, and the browser's check is welcome.
                                    */
                                    type={brand.mailDomain ? 'text' : 'email'}
                                    inputMode="email"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    onFocus={() => setAddressFocused(true)}
                                    onBlur={() => setAddressFocused(false)}
                                    autoComplete="username"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    placeholder={suffix ? 'siz' : 'siz@example.com'}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        height: '100%',
                                        padding: 0,
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        fontSize: 'var(--fs-md)',
                                    }}
                                />
                                {suffix ? (
                                    <span
                                        data-testid="address-suffix"
                                        // Not selectable and not read out twice: the input's value
                                        // is the whole address, and this only shows what will be
                                        // added to what has been typed.
                                        aria-hidden="true"
                                        style={{
                                            flex: '0 0 auto',
                                            paddingLeft: 'var(--sp-1)',
                                            color: 'var(--text-mute)',
                                            userSelect: 'none',
                                            fontSize: 'var(--fs-md)',
                                        }}
                                    >
                                        @{brand.mailDomain}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <div>
                            <label htmlFor="mail-password" style={label}>{t('login.password')}</label>
                            <input
                                id="mail-password"
                                ref={passwordRef}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                style={field}
                            />
                        </div>
                        {brand.suggestedMailboxes.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {brand.suggestedMailboxes.map((s) => {
                                    const added = accounts.some((a) => a.email === s.address);
                                    return (
                                        <button
                                            key={s.address}
                                            type="button"
                                            onClick={() => {
                                                setAddress(s.address);
                                                document.getElementById('mail-password')?.focus();
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                padding: '4px 9px',
                                                background: 'var(--chip-bg)',
                                                border: '1px solid var(--border)',
                                                borderRadius: 'var(--r-pill)',
                                                color: 'var(--text-dim)',
                                                fontSize: 'var(--fs-xs)',
                                            }}
                                        >
                                            {s.address}
                                            <span className="kicker">{added ? t('login.added') : (s.note ?? '')}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}

                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <input
                                data-testid="login-remember"
                                type="checkbox"
                                checked={remember}
                                onChange={(e) => setRemember(e.target.checked)}
                                aria-describedby="login-remember-note"
                                style={{ accentColor: 'var(--accent)', width: 18, height: 18, margin: 0, flex: '0 0 auto' }}
                            />
                            <span style={{ display: 'grid', gap: 2 }}>
                                <span style={{ fontSize: 'var(--fs-sm)' }}>{t('login.remember')}</span>
                                <span id="login-remember-note" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                                    {t('login.rememberNote')}
                                </span>
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={busy}
                            style={{
                                height: 38,
                                background: busy ? 'var(--chip-bg)' : 'var(--accent)',
                                color: busy ? 'var(--text-mute)' : 'var(--on-accent)',
                                border: 'none',
                                borderRadius: 'var(--r-btn)',
                                fontSize: 'var(--fs-md)',
                                fontWeight: 600,
                            }}
                        >
                            {busy ? t('login.working') : t('login.submit')}
                        </button>

                        {/*
                            Parolanın nereye gittiği, giriş yapmadan ÖNCE.

                            Teslim burada "parolan yalnızca posta sunucusuna iletilir" diyordu ve o
                            cümle ölçüldüğünde YANLIŞ çıktı: parola `imapx`, `smtpx`, `sievex`,
                            `davx` VE yönetim istemcisine gidiyor. İlk düzeltmem de eksikti —
                            posta ve takvim/rehber sunucularını sayıp **yönetim servisini
                            atlıyordu**, ve eksik bir sayım "yalnızca" kelimesiyle birleşince
                            verilmemesi gereken bir güvence oluyor. Metin şimdi üçünü de sayıyor.
                            Bunu bir çürütücü yakaladı.

                            Cümlenin ikinci yarısı ölçüldü ve doğru: parola diske hiç yazılmıyor,
                            oturum onu bellekte tutuyor ve çıkarken üzerine yazıyor
                            (`session.wipe`), log satırında da redakte ediliyor (`Session.String`).
                        */}
                        <div
                            data-testid="login-password-note"
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 7,
                                fontSize: 'var(--fs-xs)',
                                color: 'var(--text-mute)',
                                lineHeight: 1.5,
                            }}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto', marginTop: 2 }} aria-hidden="true">
                                <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
                                <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
                            </svg>
                            <span>{t('login.passwordNote')}</span>
                        </div>
                    </form>

                    {error ? (
                        <div
                            role="alert"
                            style={{
                                padding: '9px 11px',
                                borderRadius: 'var(--r-btn)',
                                background: 'var(--red-bg)',
                                border: '1px solid var(--red)',
                                color: 'var(--red)',
                                fontSize: 'var(--fs-sm)',
                            }}
                        >
                            {error}
                        </div>
                    ) : null}

                    {/*
                        Kurulum rehberi giriş kartının ALTINDA: ona ihtiyacı olan kişi, tanımı
                        gereği henüz giriş yapmamış olan kişidir — yeni telefonunu kuruyor.
                        Kapalı başlar, formu bozmaz.
                    */}
                    <KurulumRehberi />

                    {brand.supportText ? (
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                            {brand.supportUrl ? (
                                <a href={brand.supportUrl} target="_blank" rel="noopener noreferrer">{brand.supportText}</a>
                            ) : (
                                brand.supportText
                            )}
                        </div>
                    ) : null}
                </div>

            </div>
        </div>
    );
}
